import {IRouteProps} from "../../../../ts/manager/view/ViewInterface";
import {PostDataChoice} from "../../../../ts/data/subject/PostDataFactory";
import MapLibreMap, {MapLibreMapInstance} from "../../../../module/osm/MapLibreMap";
import React, {useRef, useState} from "react";
import {IMarkerProps, IMarkersProps, MapMarkerInstance} from "../../../../module/osm/element/MapMarker";
import {IMapClusterProps} from "../../../../module/osm/element/MapCluster";
import CardView from "../../../partial/snippet/CardView";
import AppButton from "../../../element/button/AppButton";
import {sortLocatableByNorthWest} from "../../../../ts/data/locate/LocatableDataFactory";
import RouteChoice, {IRouteChoices} from "../../../../ts/model-factory/basic/route/RouteChoice";

interface IProps
{
    routeProps:IRouteProps;
    routeChoiceList:RouteChoice[];
}

class PostViewModel
{
    requireLatLng:boolean;
    routeChoices:IRouteChoices = {};
    routeChoiceList:RouteChoice[] = [];
    postChoiceList:PostDataChoice[] = [];
    latLngChoiceOrder:number[] = [];

    constructor(requireLatLng:boolean = false){
        this.requireLatLng = requireLatLng;
    }
    applyRouteChoiceList = (routeChoiceList:RouteChoice[]) => {
        this.routeChoices = {};
        this.routeChoiceList = [];
        this.postChoiceList = [];
        this.latLngChoiceOrder = [];
        for(let routeChoice of routeChoiceList){
            const postDataChoice = routeChoice.postDataChoice;

            if(this.requireLatLng && !postDataChoice?.lngLat){
                continue;
            }
            if(postDataChoice){
                this.postChoiceList.push(postDataChoice);
            }
            this.routeChoiceList.push(routeChoice);
            this.routeChoices[routeChoice.id] = routeChoice;
        }
        const latLngPostChoiceList = this.postChoiceList.slice();
        sortLocatableByNorthWest(latLngPostChoiceList);
        for(let choice of latLngPostChoiceList){
            this.latLngChoiceOrder.push(choice.postID);
        }
    }
}

const PostMapView = (props:IProps) =>
{
    let [mapClusterProps, setMapClusterProps] = useState<IMapClusterProps|null>(null);
    let [userMarkerProps, setUserMarkerProps] = useState<IMarkerProps|null>(null);
    const [postViewModel] = useState<PostViewModel>(new PostViewModel(true));
    const mapRef = useRef<MapLibreMapInstance|null>(null);
    const [savedChoiceOrder, setSavedChoiceOrder] = useState<number[]>([]);
    const [, setSimpleState] = useState<any[]>([]);

    postViewModel.applyRouteChoiceList(props.routeChoiceList)

    const getCardContent = (marker:MapMarkerInstance):React.JSX.Element =>
    {
        const routeChoice = marker.markerID ? (postViewModel.routeChoices[marker.markerID] ?? null) : null;
        if(!routeChoice){
            return <></>
        }
        const choiceIndex = getChoiceIndex(marker);
        return <CardView
            routeChoice={routeChoice}
            extraClass={"map-group"}
            cardNumber={choiceIndex >= 0 ? choiceIndex + 1 : undefined}
            cardTotal={choiceIndex >= 0 ? postViewModel.postChoiceList.length : undefined}
        />
    }

    const requireMapClusterProps = ():IMapClusterProps =>
    {
        let clusterDidChange = !mapClusterProps;

        for(let choice of postViewModel.postChoiceList){
            if(!savedChoiceOrder.includes(choice.postID)){
                clusterDidChange = true;
            }
        }
        const currentChoiceOrder = postViewModel.latLngChoiceOrder;
        if(currentChoiceOrder.length !== savedChoiceOrder.length){
            clusterDidChange = true;
        }
        if(!clusterDidChange){
            return mapClusterProps!;
        }

        setSavedChoiceOrder(currentChoiceOrder);

        const clusterMarkersProps:IMarkersProps = {};
        for(let choice of postViewModel.postChoiceList){

            clusterMarkersProps[choice.id] = {
                marker: {
                    latitude: choice.lngLat!.lat,
                    longitude: choice.lngLat!.lng,
                },
                popupContent: (marker:MapMarkerInstance) => getCardContent(marker)
            };
        }
        mapClusterProps = {
            markers: clusterMarkersProps,
            withCountry: true,
        }
        setMapClusterProps(mapClusterProps);
        return mapClusterProps;
    }

    const requireUserMarkerProps = ():void =>
    {
        if(userMarkerProps){
            return;
        }
        userMarkerProps = {
            marker: {
                latitude: 0,
                longitude: 0,
            },
            popupContent: <div className="card">
                <div className="card-body">
                    <h2>Your location</h2>
                </div>
            </div>
        }
        setUserMarkerProps(userMarkerProps);
    }

    requireUserMarkerProps();
    requireMapClusterProps();

    const getMap = ():MapLibreMapInstance|null =>
    {
        return (mapRef.current as MapLibreMapInstance) ?? null;
    }

    const getChoiceIndex = (ofMarker:MapMarkerInstance|null = null):number =>
    {
        ofMarker = ofMarker ?? (getMap()?.selectedMarker ?? null);
        const choiceID = ofMarker?.markerType === "cluster" ? ofMarker.markerID : null;
        return choiceID ? postViewModel.latLngChoiceOrder.indexOf(parseInt(choiceID)) : -1
    }

    const showPreviousEntry = () =>
    {
        const currentIndex = getChoiceIndex();
        const currentChoiceOrder = postViewModel.latLngChoiceOrder;
        const useIndex = currentIndex > 0 ?  currentIndex - 1 : currentChoiceOrder.length - 1;
        const useChoiceID = useIndex >= 0 ? currentChoiceOrder[useIndex] : null;
        if(!useChoiceID){
            return;
        }
        getMap()?.selectClusterMarker(useChoiceID.toString(), null, true, true);
    }

    const showNextEntry = () =>
    {
        const currentIndex = getChoiceIndex();
        const currentChoiceOrder = postViewModel.latLngChoiceOrder;
        const useIndex = currentIndex < currentChoiceOrder.length - 1 ?  currentIndex + 1 : 0;
        const useChoiceID = useIndex >= 0 ? currentChoiceOrder[useIndex] : null;
        if(!useChoiceID){
            return;
        }
        getMap()?.selectClusterMarker(useChoiceID.toString(), null, true, true);
    }

    const onMapInteraction = () => {
        setSimpleState([]);
    }
    if(!Object.keys(mapClusterProps?.markers ?? {}).length){
        return <></>
    }

    return <>
        <div className="row">
            <div className="col-12">
                <MapLibreMap
                    routeProps={props.routeProps}
                    ref={mapRef}
                    mapRef={mapRef}
                    preferredMapFocus="cluster"
                    userMarker={userMarkerProps}
                    countryCode={"ie"}
                    cluster={mapClusterProps}
                    onMapInteraction={onMapInteraction}
                />
            </div>

        </div>
        <div className="row">
            {!!savedChoiceOrder.length && <>
                <div className="col-12 col-md-6">

                    {savedChoiceOrder.length > 1 && <>
                        <AppButton
                            className="btn mb-4 me-2 btn reduced"
                            disabled={!getMap()}
                            onClick={() => showPreviousEntry()}
                        >
                            Previous
                        </AppButton>
                    </>}
                    <AppButton
                        className="btn mb-4 me-2 btn reduced"
                        disabled={!getMap()}
                        onClick={() => showNextEntry()}
                    >
                        {savedChoiceOrder.length > 1 ? "Next" : "Show Location"}
                    </AppButton>
                </div>
            </>}
            <div className="col-12 col-md-6 text-md-end">
                <AppButton
                    className="btn mb-4 btn reduced"
                    disabled={!getMap() || getMap()!.isMapAtDefaultPosition()}
                    onClick={getMap() ? () => getMap()!.resetMapPosition() : undefined}
                >
                    Reset
                </AppButton>
            </div>
        </div>
    </>
}
export default PostMapView;